import {
  ContextMenu,
  DataTree,
  DataTreeProps,
  Separator,
} from '@infotech-soe/erp-ui-kit';
import { TDatabaseId } from '@infotech-soe/metaschema';
import React, { memo, MouseEvent, useCallback, useMemo } from 'react';

import { CategoryTreeType } from '../../../services/category/view-model/CategoryViewModel';
import { ConfirmationMessageType } from '../../../services/commands/UserInteractor';

import { CatalogsTreeConnectorProps } from './CatalogsTreeContainer';
import TreeTitledContainer from './TreeTitledContainer';
import { useTree } from './useTree';
import {
  SEPARATOR,
  TreeCommand,
  TreeCommandName,
  useTreeCommands,
} from './useTreeCommands';
import { useTreeContextMenu } from './useTreeContextMenu';
import { findNode } from './utils';

export type CatalogsTreeProps = CatalogsTreeConnectorProps &
  Pick<DataTreeProps<TDatabaseId>, 'className' | 'style' | 'hideCheckbox'> & {
    hideCommands?: boolean;
  };

const CatalogsTree = (props: CatalogsTreeProps): React.JSX.Element | null => {
  const {
    hideCheckbox,
    hideCommands,
    tree,
    title = '',
    onCheckToggle,
    onClearChecked,
    onRefresh,
    onSelect,
    onSetHierarchy,
    onShowConfirmation,
    ...restDataTreeProps
  } = props;
  const { activeIndex, handle } = useTree(tree);
  const { isOpen, setOpen, onContextMenu, positionX, positionY } =
    useTreeContextMenu({ selectNode: onSelect });
  const handleMove = useCallback(
    (nodeId: TDatabaseId | string, newParentId: TDatabaseId) => {
      const moveIds =
        typeof nodeId === 'string'
          ? (JSON.parse(nodeId) as { ids: TDatabaseId[] }).ids
          : [nodeId];
      const movingNodeId = moveIds[0];
      const movingToTheSamePlace =
        moveIds.some(
          id => findNode(tree.nodes, id)?.parentId === newParentId
        ) || moveIds.includes(newParentId);
      const movingIntoChildren = moveIds.some(id =>
        findNode(tree.nodes, newParentId)?.nodePath.has(id)
      );

      if (movingToTheSamePlace || movingIntoChildren) return;

      onShowConfirmation(
        typeof nodeId === 'string'
          ? ConfirmationMessageType.MOVE_IN_CATALOG
          : ConfirmationMessageType.MOVE_CATALOG,
        typeof nodeId === 'string' ? moveIds : movingNodeId,
        newParentId
      );
    },
    [onShowConfirmation, tree]
  );

  const handleCheckToggle = useCallback(
    (e: MouseEvent<HTMLDivElement> | KeyboardEvent, id?: TDatabaseId) => {
      onCheckToggle(id, e.shiftKey);
    },
    [onCheckToggle]
  );

  const { commands, clipboard } = useTreeCommands({
    treeType: CategoryTreeType.CATALOGS,
    activeNode: tree?.nodes[activeIndex],
    tree,
    hideCommands: hideCommands || !tree,
    onCheckToggle,
    onClearChecked,
    onRefresh,
    onSetHierarchy,
    onShowConfirmation,
  });

  const options = useMemo<Array<TreeCommand | Separator> | undefined>(() => {
    if (!commands) return undefined;
    const res: Array<TreeCommand | Separator> = [
      ...(hideCheckbox
        ? []
        : [
            commands[TreeCommandName.HIERARCHY],
            commands[TreeCommandName.CLEAR],
            commands[TreeCommandName.TOGGLE],
            SEPARATOR,
          ]),
      commands[TreeCommandName.CREATE],
      commands[TreeCommandName.RENAME],
      commands[TreeCommandName.DELETE],
      commands[TreeCommandName.REFRESH],
      SEPARATOR,
      commands[TreeCommandName.CUT],
      commands[TreeCommandName.PASTE],
    ].filter(cmd => !!cmd);
    return res;
  }, [commands, hideCheckbox]);

  if (!tree) return null;

  return (
    <TreeTitledContainer
      commands={options}
      hideCommands={hideCommands}
      title={title}
    >
      <DataTree
        activeId={tree.activeId}
        checked={tree.checkedNodes}
        data={tree.nodes}
        draggable
        expanded={tree.expandedNodes}
        hideCheckbox={hideCheckbox}
        hierarchyId={tree.hierarchyRootId}
        markedId={clipboard.value}
        pending={tree.pendingNodes}
        ref={handle}
        onCheckToggle={handleCheckToggle}
        onClearChecked={onClearChecked}
        onContextMenu={onContextMenu}
        onMove={handleMove}
        onRefresh={onRefresh}
        onSelect={onSelect}
        onSetHierarchy={onSetHierarchy}
        {...restDataTreeProps}
        {...(commands && {
          onAdd: commands.create.handler,
          onDelete: commands.delete.handler,
          onRename: commands.rename.handler,
        })}
      />
      <ContextMenu
        open={activeIndex !== -1 && isOpen && !hideCommands}
        options={options}
        x={positionX}
        y={positionY}
        onClick={(commandName: string) => commands?.[commandName].handler()}
        onOpen={setOpen}
      />
    </TreeTitledContainer>
  );
};

export default memo(CatalogsTree);
